// ======================================================
// Updated by: Halimunnisa
// Change: Refactored Next Stage dropdown to dynamically render LEAD_STAGES array instead of hardcoded options
// Purpose: Automatically inject Customer Acceptance into the dropdown without duplicating hardcoded arrays
// ======================================================
import React, { useMemo, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Eye,
  Phone,
  Edit2,
  UserX,
  UserCheck,
  Download,
} from "lucide-react";
import {
  DataTable,
  Avatar,
  Badge,
  Button,
  TableActionButton,
} from "../../../../components/ui";
import {
  LEAD_TABLE_HEADERS as H,
  STATUS_VARIANT_MAP,
  STATUS_COLORS,
  MANAGE_STAGES_TEXT,
  LEAD_STAGES,
} from "@/constants/globalConstants";
import { TABLE_BACKGROUNDS } from "@/constants/tableBackgrounds";

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";
const fmt = (v) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(v);

import { StageDropdown } from "./StageDropdown";
import { generateRoomQuotationPdf } from '../../../../utils/generateRoomQuotationPdf';
import toast from 'react-hot-toast';

export const LeadTableView = ({
  leads,
  isLoading,
  onLeadClick,
  onEditClick,
  onToggleDisable,
  onUpdateLead,
  updateStatus,
  onAddActivity,
  user,
  teamMembers = [],
  salesExecs = [],
  projectManagers = [],
  onLocalTabChange,
  currentTab,
}) => {
  const role = user?.role
    ?.toLowerCase()
    ?.trim()
    ?.replace(/[-_\s]+/g, "-");
  const isSalesExec =
    role === "sales-exec" || role === "sale-exec" || role === "sales-executive";
  const isSalesHead = role === "sales-head" || role === "sale-head";
  const isSuperAdmin = ["admin", "owner", "Verve-admin"].includes(role);

  const getStatusVariant = (status) =>
    STATUS_VARIANT_MAP[status?.toLowerCase()] || STATUS_VARIANT_MAP.default;

  const columns = useMemo(() => {
    const cols = [
      {
        header: "Verve ID",
        key: "customId",
        minWidth: "170px",
        render: (l) => (
          <span className="text-[10px] uppercase tracking-tight whitespace-nowrap">
            {l.customId || "—"}
          </span>
        ),
      },
      {
        header: "Contact Details",
        key: "customerName",
        render: (l) => {
          const fullName =
            l.customerName ||
            `${l.firstName || ""} ${l.lastName || ""}`.trim() ||
            "No Name";
          return (
            <div className="flex items-center gap-2.5">
              {/* Updated by: Halimunnisa - Removed Avatar component */}
              <div className="flex flex-col min-w-0">
                <span className="text-brand-dark tracking-tight text-sm leading-tight truncate">
                  {fullName}
                </span>
                <span className="text-[10px] text-slate-400 truncate flex items-center mt-0.5  tracking-tighter">
                  {/* Updated by: Halimunnisa - Added spaces between phone and email as requested */}
                  {l.phoneNumber || l.phone || "No Phone"}
                  {l.email && <span className="opacity-50 mx-1.5">|</span>}
                  {l.email && l.email.toLowerCase()}
                </span>
              </div>
            </div>
          );
        },
      },
      ...(currentTab !== "assigned" ? [
        {
          header: "Source",
          key: "source",
          render: (l) => (
            <span className="text-[10px] text-slate-500 uppercase tracking-tighter bg-slate-50 px-2 py-0.5 rounded">
              {l.source || "Manual"}
            </span>
          ),
        },
        {
          header: "Location",
          key: "location",
          render: (l) => (
            <div className="flex flex-col">
              <span className="text-[11px] text-slate-700 truncate max-w-[120px] capitalize">
                {l.city || (l.location ? l.location.split(",")[0] : "—")}
              </span>
            </div>
          ),
        },
      ] : []),
    ];
    if ((isSuperAdmin || isSalesHead || isSalesExec) && currentTab !== "assigned") {
      cols.push({
        header: "Telecaller",
        key: "teleInfo",
        render: (l) => {
          const teleInfo = l.telecallerId || l.telecaller;
          return teleInfo ? (
            <div className="flex flex-col">
              <div className="text-brand-dark text-[11px]">
                {teleInfo.name ||
                  `${teleInfo.firstName || ""} ${teleInfo.lastName || ""}`.trim() ||
                  "Unknown"}
              </div>
              <div className="text-[10px] text-slate-500">
                {l.updatedAt
                  ? new Date(l.updatedAt).toLocaleString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "—"}
              </div>
            </div>
          ) : (
            <span className="text-[11px] text-slate-400">Unassigned</span>
          );
        },
      });
    }
    if (currentTab !== "assigned") {
      cols.push({
        header: "Lead Status",
        key: "status",
      render: (l) => {
        // ======================================================
        // Updated by: Halimunnisa
        // Feature: Customer Acceptance Workflow
        // Purpose: Display Customer Acceptance status badges for Accepted, Rejected, Pending, and Revision Requested.
        // ======================================================
        const badgeClass =
          "min-w-[80px] justify-center tracking-tighter text-[9px] px-2 py-0.5 rounded-full border shadow-sm whitespace-nowrap";

        if (l.leadStatus === "customer_acceptance") {
          const isFullyApproved = l.initialQuotationApproval?.approvalStatus === "fully_approved";
          if (isFullyApproved) {
            return (
              <Badge variant="success" label="Fully Approved" className={badgeClass} />
            );
          }
          return (
            <Badge variant="amber" label="Pending Approval" className={badgeClass} />
          );
        }

        if (
          l.leadStatus === "cust_acceptance" &&
          l.customerAcceptance?.status
        ) {
          const caStatus = l.customerAcceptance.status;

          if (caStatus === "Accepted")
            return (
              <Badge
                variant="success"
                label="Client Accepted"
                className={badgeClass}
              />
            );
          if (caStatus === "Rejected")
            return (
              <Badge variant="danger" label="Rejected" className={badgeClass} />
            );
          if (caStatus === "Revision Requested")
            return (
              <Badge
                variant="warning"
                label="Revision Requested"
                className={badgeClass}
              />
            );
          if (caStatus === "Pending")
            return (
              <Badge
                variant="secondary"
                label="Pending Customer"
                className={badgeClass}
              />
            );
        }

        const effectiveStatus = (l.leadStatus || "new").toLowerCase();
        return (
          <Badge
            variant={getStatusVariant(effectiveStatus)}
            label={
              effectiveStatus === "new"
                ? "New Lead"
                : effectiveStatus.replace("_", " ")
            }
            className="min-w-[80px] justify-center tracking-tighter text-[9px] px-2 py-0.5 rounded-full border shadow-sm whitespace-nowrap"
          />
        );
      },
    });
    }

    if (currentTab !== "assigned") {
      cols.push({
        header: "Last Note",
        key: "lastNote",
      className: "min-w-[250px]",
      render: (l) => {
        let displayNote = "";
        if (currentTab && currentTab !== "all") {
          displayNote = l.stageNotes?.[currentTab] || "";
        } else {
          displayNote = l.lastNote || l.notes || "";
        }
        if (displayNote && typeof displayNote === "string") {
          if (displayNote.includes(" | Note: ")) {
            displayNote = displayNote.split(" | Note: ")[1].trim();
          } else if (
            displayNote.startsWith("Lead Status changed") ||
            displayNote.startsWith("Design Status changed") ||
            displayNote.startsWith("Appointment scheduled") ||
            displayNote.startsWith("Lead assigned") ||
            displayNote.startsWith("Lead reassigned")
          ) {
            displayNote = "";
          }
        }
        return (
          <span className="text-[11px] text-slate-600 italic block leading-normal py-1">
            {displayNote || "—"}
          </span>
        );
      },
    });
    }

    if (isSuperAdmin || isSalesHead || isSalesExec) {
      cols.push({
        header: "Sales Exec",
        key: "assignedTo",
        render: (l) => {
          const currentAssignedId = l.assignedTo?._id || l.assignedTo || "";
          const isAssigned = salesExecs.some(
            (u) => u._id === currentAssignedId,
          );
          return (
            <div className="w-[180px]">
              <select
                value={isAssigned ? currentAssignedId : ""}
                disabled={isAssigned}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) =>
                  onUpdateLead(l._id, {
                    assignedTo: e.target.value,
                    status: e.target.value
                      ? "assigned"
                      : l.leadStatus || l.status,
                  })
                }
                className={`w-full text-[11px] font-medium bg-white border border-slate-200 rounded-md px-3 py-1.5 outline-none transition-all text-slate-700 ${isAssigned ? "opacity-70 bg-slate-50 cursor-not-allowed" : "cursor-pointer hover:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"}`}
              >
                {!isAssigned && <option value="" disabled>Select Sales Exec</option>}
                {salesExecs.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          );
        },
      });

      // Next Stage Dropdown
      if (currentTab !== "assigned") {
      cols.push({
        //updated by Halimuunnisa on 2024-06-17
        //chnaged header from "Next Stage" to "Status" to reflect new Customer Acceptance stage and avoid confusion with leadStatus column
        header: "Status",
        key: "nextStage",
        render: (l) => {
          const currentStatus = l.leadStatus || "new";
          const isAssigned = !!(l.assignedTo?._id || l.assignedTo || l.salesPerson || l.salesExecutive);

          // ======================================================
          // Updated by: Halimunnisa
          // Feature: Customer Acceptance Workflow
          // Purpose: Lock workflow progression dropdown if customer has rejected the quotation.
          // ======================================================
          const isLocked =
            l.isDisabled || l.customerAcceptance?.status === "Rejected";

          const isStatusDisabled = isLocked || !isAssigned;

          // DEBUG LOG REQUESTED BY USER
          if (l.customId === "VER-2627-00003" || !isAssigned) {
            console.log("Lead", l);
            console.log("leadStatus", l.leadStatus);
            console.log("assignedTo", l.assignedTo);
            console.log("salesExecutive", l.salesExecutive);
            console.log("isLocked:", isLocked, "isAssigned:", isAssigned);
          }

          return (
            // updated by nithin: increased width from 140px to 170px to fit larger labels like FINAL QUOTATION APPROVAL
            <div className="w-[170px]" onClick={(e) => e.stopPropagation()}>
              <select
                value={currentStatus}
                onChange={(e) => updateStatus(l, e.target.value)}
                className={`w-full text-[10px] uppercase text-brand-dark bg-white border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-blue ${isStatusDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-brand-blue transition-colors'}`}
                disabled={isStatusDisabled}
              >
                {/* ======================================================
                Updated by: Halimunnisa
                Added Customer Acceptance stage to workflow dropdown
                Dynamically mapped from LEAD_STAGES
                ====================================================== */}
                {LEAD_STAGES.map((stage, idx) => {
                  let isDisabledOption = false;
                  if (l.initialQuotationApproval?.approvalStatus === 'fully_approved') {
                    let thresholdIdx = LEAD_STAGES.findIndex(s => s.key === 'customer_acceptance');
                    
                    if (l.customerAcceptance?.status === 'Accepted') {
                       // Disable all lead stages (including customer_acceptance and cust_acceptance)
                       thresholdIdx = LEAD_STAGES.length;
                    }
                    
                    if (idx < thresholdIdx && stage.key !== currentStatus) {
                      isDisabledOption = true;
                    }
                  }

                  return (
                    <option key={stage.key} value={stage.key} disabled={isDisabledOption}>
                      {stage.label}
                    </option>
                  );
                })}
              </select>
            </div>
          );
        },
      });
      }
    }

    /*
    if (isSuperAdmin || isSalesHead) {
      cols.push({
        header: "Assign PM",
        key: "projectManager",
        render: (l) => {
          const currentPMId = l.projectManager?._id || l.projectManager || "";
          const currentLeadStatus = (l.leadStatus || l.status || "new").toLowerCase();
          const isPMDisabled = !!currentPMId;
          return (
            <div className="w-[180px]">
              <select
                value={currentPMId}
                disabled={isPMDisabled}
                onClick={e => e.stopPropagation()}
                onChange={e => onUpdateLead(l._id, { projectManager: e.target.value })}
                className={`w-full text-[11px] font-medium bg-white border border-slate-200 rounded-md px-3 py-1.5 outline-none focus:ring-2 focus:ring-brand-blue/20 transition-all ${isPMDisabled ? "cursor-not-allowed opacity-70 bg-slate-50 text-slate-500" : "cursor-pointer hover:border-brand-blue text-emerald-700"}`}
              >
                <option value="">Unassigned</option>
                {projectManagers.map(u => (
                  <option key={u._id} value={u._id}>{u.name}</option>
                ))}
              </select>
            </div>
          );
        }
      });
    }
    */

    cols.push({
      header: "Actions",
      key: "actions",
      render: (l) => {
        const isFullyApproved = l.initialQuotationApproval?.approvalStatus === 'approved' || l.initialQuotationApproval?.approvalStatus === 'fully_approved' || l.leadStatus === 'customer_acceptance' || l.leadStatus === 'cust_acceptance' || l.leadStatus === 'project_assigned';

        const handleDownload = async (e) => {
          e.stopPropagation();
          try {
            toast.loading("Generating Quotation PDF...", { id: "pdf-toast" });
            
            let savedItems = l.items || l.quotationItems || l.initialQuotationItems || [];
            if (l.quotationData && l.quotationData.items) {
              savedItems = l.quotationData.items;
            }

            const roomsData = savedItems.map(roomData => ({
              roomName: roomData.room || 'Room',
              items: (roomData.items || []).map(item => ({
                name: item.name,
                colour: item.color || item.colour || '-',
                type: item.category === 'internalType' ? 'Internal Type' : 
                      item.category === 'shutterType' ? 'Shutter Type' : 
                      item.category === 'blumItems' ? 'Hardware' : 
                      item.category === 'customItems' ? 'Custom' : item.category || item.type || '-',
                warranty: item.warranty || '-',
                amount: fmt(parseFloat(item.unitPrice || item.amount || 0)),
                details: item.measurementInfo ? item.measurementInfo.split(' | ') : [],
                quantity: item.quantity || 1
              }))
            }));

            const amount = l.quotationAmount || l.amount || 0;
            const templateData = {
              projectName: l.projectName || "-",
              clientName: l.customerName || `${l.firstName || ''} ${l.lastName || ''}`.trim() || "-",
              date: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
              site: l.city || l.location || "-",
              quotationNo: l.customId ? `VK/${l.customId.toUpperCase()}` : "-",
              contactRef: l.phoneNumber || l.phone || "-",
              designer: l.projectManager?.name || l.assignedTo?.name || "-",
              rooms: roomsData,
              totals: {
                total: fmt(amount),
                discount: "0",
                grandTotal: fmt(amount)
              }
            };

            await generateRoomQuotationPdf(l, templateData);
            toast.success("Quotation Downloaded Successfully!", { id: "pdf-toast" });
          } catch (error) {
            console.error("PDF generation failed:", error);
            toast.error("Failed to generate PDF", { id: "pdf-toast" });
          }
        };

        return (
          <div className="flex items-center gap-2 justify-end">
            <TableActionButton
              icon={Eye}
              variant="primary"
              onClick={(e) => {
                e.stopPropagation();
                onLeadClick(l);
              }}
              title="View Details"
            />
            {currentTab !== 'new' && currentTab !== 'assigned' && currentTab !== 'appointment_scheduled' && (
              <TableActionButton
                icon={Edit2}
                variant="orange"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditClick(l);
                }}
                title="Edit Lead"
              />
            )}
            {isFullyApproved && currentTab !== 'assigned' && currentTab !== 'appointment_scheduled' && (
              <TableActionButton
                icon={Download}
                variant="success"
                onClick={handleDownload}
                title="Download Quotation"
              />
            )}
          </div>
        );
      },
    });

    return cols;
  }, [
    isSalesExec,
    isSalesHead,
    isSuperAdmin,
    onAddActivity,
    onEditClick,
    onLeadClick,
    onToggleDisable,
    onUpdateLead,
    projectManagers,
    salesExecs,
    getStatusVariant,
  ]);

  return (
    <DataTable
      columns={columns}
      data={leads}
      isLoading={isLoading}
      getRowClassName={(idx, row) =>
        row?.isDisabled ? "opacity-50 bg-slate-50/50" : ""
      }
      className="border-none shadow-none rounded-none"
    />
  );
};
